import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Users, UserPlus, X } from "lucide-react";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { engagementName } from "@/lib/engagementLabel";   // Batch 15 · N5 (F-119)
import { asArray } from "@/lib/asArray";

/**
 * Firm-Admin dialog to manage which users can see an engagement.
 *
 * Members list = users with explicit access. Firm Admin (Partner + Admin
 * roles) always see every engagement in their tenant regardless of the
 * list — that's enforced by the backend and displayed here for clarity.
 */
export default function EngagementTeamDialog({ eng, open, onOpenChange }) {
  const { t, lang } = useI18n();
  // Batch 30 · M73: what this user may do here comes from the server (creator / level rule) — not from the role alone
  const [rights, setRights] = useState(null);
  const canManage = !!rights?.can_add;
  // Remove shows per member from the server's answer (Batch 32 · T8: not the last member, not the engagement partner, …)
  const removable = new Set(asArray(rights?.removable_user_ids));
  const [addError, setAddError] = useState("");
  const [members, setMembers] = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState("");

  const load = async () => {
    if (!eng) return;
    try {
      const [m, u, r] = await Promise.all([
        api.get(`/engagements/${eng.id}/members`),
        // RBAC pass 2026-09-14: switched from /users (firm-admin only)
        // to /users/team-directory — {id, name, role} only. This
        // dialog is opened by Reviewer + Audit Manager too, so
        // /users would 403 for them post-fix.
        api.get("/users/team-directory"),
        api.get(`/engagements/${eng.id}/team-rights`, { _silent: true }).catch(() => ({ data: null })),
      ]);
      setRights(r?.data || null);
      setMembers(m.data || []);
      setTenantUsers(u.data || []);
    } catch (e) { toast.error(toErrMsg(e, t("common.failed"))); }
  };
  const loadOnOpen = useEffectEvent(load);
  useEffect(() => { if (open) loadOnOpen(); }, [open, eng?.id, loadOnOpen]);

  const memberIds = new Set(members.map((m) => m.user_id));
  const allowedRoles = Array.isArray(rights?.addable_roles) ? new Set(rights.addable_roles) : null;   // null = any role
  const addable = tenantUsers.filter((u) => !memberIds.has(u.id) && (!allowedRoles || allowedRoles.has(u.role)));

  const add = async () => {
    if (!picking) return;
    setBusy(true);
    setAddError("");
    try {
      await api.post(`/engagements/${eng.id}/members`, { user_id: picking }, { _silent: true });
      toast.success(t("engTeam.added"));
      setPicking("");
      await load();
    } catch (e) {
      // a refusal stays on the dialog (EN + AR) — not a toast that disappears
      const ar = lang === "ar" && e?.response?.data?.detail_ar;
      setAddError(ar || toErrMsg(e, t("common.failed")));
    }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    if (!await askConfirm(t("engTeam.removeConfirm").replace("{name}", u.name || u.email))) return;
    setAddError("");
    try {
      await api.delete(`/engagements/${eng.id}/members/${u.user_id}`, { _silent: true });
      toast.success(t("engTeam.removed")); await load();
    } catch (e) {
      // PR 44 item 10: a refused removal stays on the dialog (EN + AR), like a refused add
      const ar = lang === "ar" && e?.response?.data?.detail_ar;
      setAddError(ar || toErrMsg(e, t("common.failed")));
    }
  };
  const roleText = (r) => (r?.title ? `${r.role} · ${r.title}` : r?.role);   // PR 44 item 8: Partner · Director

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="engagement-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-emerald-600" />
            {t("engTeam.title")} — {engagementName(eng, t)}
          </DialogTitle>
          <DialogDescription>
            {t("engTeam.desc")}
          </DialogDescription>
        </DialogHeader>

        {rights && !rights.full && rights.can_add && (
          <p className="text-[11px] text-muted-foreground" data-testid="team-level-note">
            {t("engTeam.levelNote").replace("{roles}", asArray(rights.addable_roles).join(", "))}
          </p>
        )}
        {rights && !rights.can_add && (
          <p className="text-[11px] text-muted-foreground" data-testid="team-read-only">{t("engTeam.readOnly")}</p>
        )}
        {addError && (
          <div role="alert" className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="team-add-error">
            <div className="font-medium">{t("engTeam.addRefused")}</div>
            <div>{addError}</div>
          </div>
        )}
        {canManage && (
          <div className="flex items-center gap-2">
            <Select value={picking} onValueChange={setPicking}>
              <SelectTrigger data-testid="team-add-picker" className="flex-1">
                <SelectValue placeholder={addable.length ? t("engTeam.pickUser") : t("engTeam.allInvited")} />
              </SelectTrigger>
              <SelectContent>
                {addable.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name || u.email}
                    <span className="ms-2 text-[10px] text-muted-foreground">{roleText(u)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" disabled={!picking || busy}
                      onClick={add} data-testid="team-add-btn"
                      className="bg-slate-900 hover:bg-slate-800">
              <UserPlus className="me-1 h-3 w-3" /> {t("engTeam.add")}
            </Button>
          </div>
        )}

        <div className="rounded border">
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-start">{t("common.name")}</th>
                <th className="px-3 py-2 text-start">{t("common.role")}</th>
                <th className="px-3 py-2 text-end"></th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-muted-foreground">
                  {t("engTeam.empty")}
                </td></tr>
              )}
              {members.map((m) => (
                <tr key={m.user_id} className="border-b" data-testid={`team-row-${m.user_id}`}>
                  <td className="px-3 py-2 font-medium">{m.name || m.email}</td>
                  <td className="px-3 py-2 text-xs" data-testid={`team-role-${m.user_id}`}>{roleText(m)}</td>
                  <td className="px-3 py-2 text-end">
                    {removable.has(m.user_id) && (
                      <Button size="sm" variant="ghost" onClick={() => remove(m)}
                                data-testid={`team-remove-${m.user_id}`}>
                        <X className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
